import { useEffect, useState } from "react";
import { ListOrdered, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import ModalPortal from "@/components/ui/modal-portal";
import { API_BASE } from "./constants";

/**
 * « Ajouter à un itinéraire » depuis un apport de fertilisation ou de biostimulant :
 * l'apport devient une étape d'un itinéraire existant (à la suite) ou d'un nouvel itinéraire.
 */
export default function AddToItineraryButton({ kind, item, color = "#2D6A4F" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 rounded-lg" style={{ color }}
        onClick={() => setOpen(true)} data-testid={`add-itinerary-${kind}-${item[`${kind}_id`]}`}>
        <ListOrdered className="w-3 h-3" /> Ajouter à un itinéraire
      </Button>
      {open && <AddToItineraryModal kind={kind} item={item} color={color} onClose={() => setOpen(false)} />}
    </>
  );
}

function AddToItineraryModal({ kind, item, color, onClose }) {
  const [list, setList] = useState(null);
  const [target, setTarget] = useState("new");
  const [name, setName] = useState("");
  const [delay, setDelay] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    authFetch(`${API_BASE}/itineraries`)
      .then((r) => (r.ok ? r.json() : { itineraries: [] }))
      .then((d) => {
        const mine = (d.itineraries || []).filter((it) => it.can_edit && !it.is_template);
        setList(mine);
        if (mine.length) { setTarget(mine[0].itinerary_id); setDelay(15); }
      })
      .catch(() => setList([]));
  }, []);

  const isNew = target === "new";
  const submit = async () => {
    if (isNew && !name.trim()) { toast.error("Donnez un nom au nouvel itinéraire"); return; }
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/itineraries/add-carnet-item`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, item_id: item[`${kind}_id`], itinerary_id: isNew ? null : target, name: isNew ? name.trim() : null, delay_days: Number(delay) || 0 }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Ajout impossible");
      toast.success(`Ajouté à « ${d.name} »`, { description: `${(d.steps || []).length} étape${(d.steps || []).length > 1 ? "s" : ""} dans l'itinéraire` });
      onClose();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const current = list?.find((it) => it.itinerary_id === target);
  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[2200] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="add-itinerary-modal">
        <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2 px-4 py-3 text-white" style={{ background: "#2D6A4F" }}>
            <ListOrdered className="w-4 h-4" />
            <div className="text-sm font-bold flex-1">Ajouter à un itinéraire</div>
            <button onClick={onClose} className="p-1 rounded hover:bg-white/15" aria-label="Fermer"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-4 space-y-3">
            <div className="rounded-lg p-2.5 text-[12px]" style={{ background: `${color}12` }}>
              <span className="font-semibold">{item.product_name}</span>
              {item.dose ? <span className="text-gray-600"> · {item.dose} {item.unit}</span> : null}
              <span className="text-gray-500"> · {kind === "fertilisation" ? "Fertilisation" : "Biostimulant"}</span>
            </div>
            {list === null ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : (
              <div className="space-y-1.5">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Itinéraire</div>
                {list.map((it) => (
                  <label key={it.itinerary_id} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[12.5px] cursor-pointer ${target === it.itinerary_id ? "border-emerald-700 bg-emerald-50/60" : "border-black/10"}`}>
                    <input type="radio" className="accent-emerald-700" checked={target === it.itinerary_id} onChange={() => { setTarget(it.itinerary_id); setDelay(15); }} />
                    <span className="flex-1 min-w-0 truncate">{it.name}</span>
                    <span className="text-[11px] text-gray-500 shrink-0">{(it.steps || []).length} étape{(it.steps || []).length > 1 ? "s" : ""}</span>
                  </label>
                ))}
                <label className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[12.5px] cursor-pointer ${isNew ? "border-emerald-700 bg-emerald-50/60" : "border-black/10"}`}>
                  <input type="radio" className="accent-emerald-700" checked={isNew} onChange={() => { setTarget("new"); setDelay(0); }} />
                  <span>Nouvel itinéraire</span>
                </label>
                {isNew && (
                  <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus placeholder="Nom de l'itinéraire (ex : Fertilisation canne 2027)"
                    className="w-full h-9 border border-black/15 rounded-md px-2 text-[12.5px]" data-testid="add-itinerary-name" />
                )}
              </div>
            )}
            {!isNew && (
              <label className="flex items-center gap-2 text-[12.5px] flex-wrap">
                Placer cette étape
                <input type="number" min={0} max={730} value={delay} onChange={(e) => setDelay(e.target.value)}
                  className="h-8 w-16 border border-black/15 rounded-md px-2 text-[12.5px]" data-testid="add-itinerary-delay" />
                jours après la dernière étape{current?.steps?.length ? ` (${current.steps[current.steps.length - 1].product?.commercial_name})` : ""}
              </label>
            )}
            <p className="text-[11px] text-gray-500">Produit, dose et méthode sont repris de cet apport. Vous pourrez tout ajuster dans « Itinéraires techniques ».</p>
          </div>
          <div className="flex justify-between gap-2 px-4 py-3 border-t border-black/5">
            <button onClick={onClose} className="h-9 px-4 rounded-lg border border-black/15 text-xs font-semibold">Annuler</button>
            <button onClick={submit} disabled={busy || list === null} className="h-9 px-4 rounded-lg text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: "#2D6A4F" }} data-testid="add-itinerary-submit">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Ajouter
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
